import { useEffect, useState } from 'react';
import { collection, collectionGroup, getAggregateFromServer, sum } from 'firebase/firestore';
import { firestore } from '@/lib/firebase';
import { reportNonFatal, serializeError } from '@/lib/errorReport';
import { registeredTestTenantIds } from '@/features/tenants/testTenantFilter';

const TAG = 'admin/usage/allTimePrintedGlobal';

export type AllTimePrintedGlobalState =
  | { kind: 'LOADING' }
  | { kind: 'READY'; total: number }
  | { kind: 'ERROR' };

/**
 * Total de etiquetas impressas em TODAS as empresas de cliente, desde sempre —
 * soma de `labelsPrinted` sobre o collection group `months`, via aggregation
 * query, MENOS o mesmo total de cada tenant de teste cadastrado (#1008).
 *
 * Origem: #297/#298/#299 — commit ffde3b389, 2026-08-31 (o total global que
 * faltava no BI).
 *
 * Usado por: `AllTimePrintedCard.tsx`, dentro de `UsageDashboardPage.tsx`
 * (rota `/dashboard/usage`; web `apps/admin`, super-admin). Autorizado por
 * `match /{path=**}/months/{yyyymm}` de `firebase/firestore.rules`.
 *
 * ## Qual dos números é este
 *
 * É o **A** (billing/BI), escrito pelo trigger `recordLabelPrinted` em
 * `tenants/{tenantId}/billing/usage/months/{yyyymm}`: bucket em UTC, ancorado
 * no `Date.now()` do trigger. **Não** é o número que o cliente vê no Monitor
 * — aquele é o **B** (`daily_ops.totals.printed`), no fuso do tenant e
 * ancorado no `printedAtMillis`. Os dois divergem por construção; as quatro
 * divergências estão em `docs/label-counters.md`. Não "corrija" um pelo outro.
 *
 * ## Por que `months` e não a série diária global
 *
 * `aggregations/daily/days/{yyyymmdd}.labels.printedToday` só existe desde o
 * deploy do agregador e tem dias `null`. Somar os buckets mensais é exato
 * desde a primeira impressão do primeiro tenant, sem backfill.
 *
 * ## Fragilidade do collection group (a mesma classe de bug do CG `days`)
 *
 * O CG `months` casa também `aggregations/cohorts/months/{yyyymm}`. Esses docs
 * não têm `labelsPrinted` e `sum()` ignora campo ausente, então contribuem 0 —
 * mas isso é um acordo tácito, não uma garantia do Firestore. Há tripwire em
 * `packages/types/src/usagePaths.test.ts` que quebra se o schema de cohort
 * ganhar o campo.
 *
 * **Escape hatch**: se o índice CG virar problema, ou se aparecer uma terceira
 * coleção `months` com `labelsPrinted`, troque a aggregation query por
 * `getDocs(collectionGroup(firestore, 'months'))` + soma client-side filtrando
 * `d.ref.path` — não precisa de índice nenhum (CG sem filtro nem `orderBy`
 * ordena por `__name__`, sempre indexado) e é imune por construção. Custo: N
 * leituras em vez de N/1000.
 *
 * ## Índice
 *
 * Exige o `fieldOverride` de `months.labelsPrinted` em escopo
 * `COLLECTION_GROUP` (`firebase/firestore.indexes.json`) — índice CG
 * single-field não é automático. Sem ele: `failed-precondition`.
 *
 * Leitura **one-shot**: número cumulativo, muda pouco dentro de uma sessão.
 * Aggregation query só resolve no servidor: não há caminho de cache, então
 * offline cai em `ERROR`. O card renderiza um `Alert` nesse estado — nunca
 * `0`, que seria mentira. (O irmão `useAllTimePrinted` do app cliente mostra
 * "—" no lugar; aqui a superfície é interna e o erro pode ser explícito.)
 *
 * ## O que "todas as empresas" inclui
 *
 * Tenants de demonstração e tenants `DELETED` entram. Uma aggregation query
 * cross-tenant não tem como filtrar por `isDemo`/`status` — o filtro teria que
 * estar no próprio bucket. `dailyInactiveTenantsReport` exclui os dois; este
 * número não.
 *
 * **Tenant de teste NÃO entra** (#1008, decisão do owner 2026-10-19: o volume
 * do simulador de uso, IT-98765432103 e BR-01010001000180, sai de todos os
 * painéis). Como o CG não filtra por caminho, o hook SUBTRAI: uma aggregation
 * query de coleção (`tenants/{id}/billing/usage/months`) por id do cadastro
 * (`registeredTestTenantIds`, hoje ~10; id sem documento soma 0). Índice: o
 * mesmo `fieldOverride` de `months.labelsPrinted` tem também o escopo
 * `COLLECTION`. Sandbox não cadastrado continua somando — aceito na #1008.
 * Qualquer uma das consultas falhando → `ERROR` (nunca um total sem a
 * subtração, que voltaria a contar o simulador em silêncio).
 *
 * Issues: #297/#298/#299 (origem), #1008 (tenant de teste fora).
 *
 * Histórico:
 *  - 2026-10-19 20:57 +0200 — (#1008) subtrai o volume dos tenants de teste.
 */
export function useAllTimePrintedGlobal(): AllTimePrintedGlobalState {
  const [state, setState] = useState<AllTimePrintedGlobalState>({ kind: 'LOADING' });

  useEffect(() => {
    let active = true;
    const spec = { total: sum('labelsPrinted') };
    const finite = (n: number): number => (Number.isFinite(n) ? n : 0);
    Promise.all([
      getAggregateFromServer(collectionGroup(firestore, 'months'), spec),
      // #1008: total all-time de cada tenant de teste, para subtrair.
      ...registeredTestTenantIds().map((id) =>
        getAggregateFromServer(
          collection(firestore, 'tenants', id, 'billing', 'usage', 'months'),
          spec,
        ),
      ),
    ])
      .then(([globalSnap, ...testSnaps]) => {
        if (!active) return;
        const testTotal = testSnaps.reduce((acc, s) => acc + finite(s.data().total), 0);
        const total = finite(globalSnap.data().total) - testTotal;
        setState({ kind: 'READY', total: Math.max(0, total) });
      })
      .catch((err: unknown) => {
        if (!active) return;
        // Sem DevTools do operador em produção (CLAUDE.md §Observabilidade):
        // a falha precisa chegar ao PostHog, não só ao console. Um
        // `failed-precondition` aqui significa índice ainda construindo;
        // `permission-denied`, rules não deployadas.
        reportNonFatal(`${TAG}_failed`, serializeError(err));
        setState({ kind: 'ERROR' });
      });
    return (): void => {
      active = false;
    };
  }, []);

  return state;
}
